<template>
  <teleport :to="target">
    <transition name="page-overlay">
      <div
        v-if="open"
        ref="region"
        class="page-overlay"
        role="region"
        tabindex="-1"
        :aria-label="label"
        :data-test="dataTest"
      >
        <slot />
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import { useFullscreenTarget } from '@/composables/useFullscreenTarget'
import { useOverlayChrome } from '@/composables/useOverlayChrome'

const props = withDefaults(defineProps<{ open: boolean; label: string; dataTest?: string }>(), {
  dataTest: 'page-overlay',
})

const emit = defineEmits<{ (e: 'close'): void }>()

const target = useFullscreenTarget()

const region = useTemplateRef<HTMLElement>('region')

useOverlayChrome({
  isOpen: computed(() => props.open),
  onClose: () => emit('close'),
  region,
})
</script>

<style lang="scss" scoped>
:global(body.overlay-open) {
  @apply tw-fixed tw-w-full tw-overflow-hidden;
}

// Left offset mirrors .padded-for-sidebar so the sidebar stays usable behind the overlay.
.page-overlay {
  @apply tw-fixed tw-inset-y-0 tw-right-0 tw-z-20 tw-bg-white;
  // Below the app header pill (z-21), the sidebar's only reopen control while collapsed.
  left: var(--sidebar-width, 0px);
  transition: left 0.3s ease;
}

.page-overlay-enter-active,
.page-overlay-leave-active {
  transition: all 0.25s ease;
}

.page-overlay-enter-from,
.page-overlay-leave-to {
  opacity: 0;
  transform: translateX(2%);
}
</style>
